<script setup lang="ts">
import {
  PhArrowLeft as ArrowLeftIcon,
  PhCheckCircle as CheckCircleIcon,
  PhPalette as PaletteIcon,
  PhPencilSimple as EditIcon,
  PhRuler as RulerIcon,
  PhUserCircle as UserIcon,
  PhWarningCircle as WarningIcon,
  PhWrench as WrenchIcon,
} from '@phosphor-icons/vue'
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProductImage from '../components/ProductImage.vue'
import StatusState from '../components/StatusState.vue'
import { ApiError, api } from '../services/api'
import {
  emptyCustomizationContact,
  useCustomizationStore,
  type CustomizationContact,
} from '../stores/customization'
import { useSessionStore } from '../stores/session'
import type { ConfigurationValidation, InquiryPayload, Product, ProductConfiguration } from '../types'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const customization = useCustomizationStore()
const product = ref<Product | null>(null)
const configuration = ref<ProductConfiguration | null>(null)
const loading = ref(true)
const error = ref('')
const validating = ref(false)
const validation = ref<ConfigurationValidation | null>(null)
const submitting = ref(false)
const submitError = ref('')
const submittedNo = ref('')
const contact = reactive<CustomizationContact>(emptyCustomizationContact())

const selectedHardware = computed(() =>
  product.value?.hardwarePackages.find((item) => item.code === configuration.value?.hardwareCode),
)
const selectedPartColors = computed(() => {
  if (!product.value || !configuration.value) return []
  return (product.value.customizationProfile?.specification.parts || []).map((part) => ({
    part,
    color: product.value!.colors.find(
      (item) => item.code === configuration.value!.partColors[part.id],
    ),
  }))
})

function centimeters(value: number) {
  return Number((value / 10).toFixed(1))
}

function editLink(hash: string) {
  return { name: 'product', params: { slug: route.params.slug }, hash }
}

async function validateDraft() {
  if (!configuration.value) return false
  validating.value = true
  try {
    validation.value = await api.validateConfiguration({
      ...configuration.value,
      partColors: { ...configuration.value.partColors },
    })
    return validation.value.valid
  } catch (cause) {
    validation.value = {
      valid: false,
      requiresManualReview: false,
      messages: [{
        level: 'error',
        message: cause instanceof Error ? cause.message : '方案校验失败，请返回修改。',
      }],
    }
    return false
  } finally {
    validating.value = false
  }
}

async function loadReview() {
  loading.value = true
  error.value = ''
  try {
    const value = await api.getProduct(String(route.params.slug))
    if (!value.customizationProfile) {
      throw new Error('当前商品还没有开放专属定制流程。')
    }
    const draft = customization.draft
    if (
      !draft
      || draft.slug !== value.slug
      || draft.productId !== value.id
      || draft.productVersionId !== value.versionId
      || draft.specificationId !== value.customizationProfile.specificationId
    ) {
      await router.replace(editLink('#custom-colors'))
      return
    }
    product.value = value
    configuration.value = customization.configurationFor(value)
    Object.assign(contact, customization.contactFor(value))
    await validateDraft()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '定制方案加载失败。'
  } finally {
    loading.value = false
  }
}

function saveContact() {
  if (!product.value) return
  customization.saveContact(product.value, { ...contact })
}

async function submitCustomization() {
  if (!product.value || !configuration.value || submitting.value) return
  submitError.value = ''
  saveContact()
  if (!contact.contactName.trim()) {
    submitError.value = '请填写联系人。'
    return
  }
  if (!session.isAuthenticated) {
    await router.push({
      name: 'login',
      query: {
        intent: 'customization',
        redirect: route.fullPath,
      },
    })
    return
  }

  submitting.value = true
  try {
    if (!(await validateDraft())) {
      throw new ApiError(
        { code: 'CONFIGURATION_INVALID', message: '定制方案已发生变化，请返回修改后再提交。' },
        422,
      )
    }
    const payload: InquiryPayload = {
      configuration: {
        ...configuration.value,
        partColors: { ...configuration.value.partColors },
      },
      contactName: contact.contactName.trim(),
      notes: [
        contact.wechatId.trim() ? `微信：${contact.wechatId.trim()}` : '',
        contact.notes.trim(),
      ].filter(Boolean).join('；') || undefined,
    }
    const result = await api.createInquiry(payload)
    submittedNo.value = result.inquiryNo
    customization.clear()
  } catch (cause) {
    submitError.value = cause instanceof Error ? cause.message : '定制方案提交失败。'
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  document.body.classList.add('has-customization-review-bar')
  loadReview()
})

onBeforeUnmount(() => {
  document.body.classList.remove('has-customization-review-bar')
})
</script>

<template>
  <section class="section section--review">
    <div class="container customization-review">
      <RouterLink class="back-link" :to="editLink('#custom-colors')">
        <ArrowLeftIcon :size="18" aria-hidden="true" />
        返回修改定制
      </RouterLink>

      <StatusState v-if="loading" kind="loading" />
      <StatusState
        v-else-if="error"
        kind="error"
        title="专属定制暂时无法显示"
        :message="error"
        @retry="loadReview"
      />
      <div v-else-if="product && configuration" class="customization-review__content">
        <header class="customization-review__heading">
          <span class="category-label">方案提交前确认</span>
          <h1>您的专属定制</h1>
          <p>请逐项确认选色、尺寸和五金。提交后，客服会与您二次确认再进入后续流程。</p>
        </header>

        <section class="review-section review-product" aria-labelledby="review-product-title">
          <ProductImage :src="product.image" :alt="`${product.name}商品图`" :position="product.imagePosition" />
          <div>
            <span>{{ product.category }}{{ product.secondaryCategory ? ` / ${product.secondaryCategory}` : '' }}</span>
            <h2 id="review-product-title">{{ product.name }}</h2>
            <p>{{ product.summary }}</p>
          </div>
        </section>

        <section class="review-section" aria-labelledby="review-colors-title">
          <div class="review-section__heading">
            <div><PaletteIcon :size="22" /><h2 id="review-colors-title">定制颜色</h2></div>
            <RouterLink :to="editLink('#custom-colors')"><EditIcon :size="17" />修改</RouterLink>
          </div>
          <div class="review-color-list">
            <div v-for="item in selectedPartColors" :key="item.part.id">
              <span
                v-if="item.color"
                class="color-option__swatch"
                :style="{ background: item.color.swatch }"
              >
                <img v-if="item.color.swatchImage" :src="item.color.swatchImage" alt="" />
              </span>
              <span v-else class="review-color-list__empty" aria-hidden="true"></span>
              <span>
                <small>{{ item.part.name }}</small>
                <strong>{{ item.color?.name || '未选择' }}</strong>
                <em v-if="item.color">{{ item.color.supplierCode || item.color.code }}</em>
              </span>
            </div>
          </div>
        </section>

        <section class="review-section" aria-labelledby="review-size-title">
          <div class="review-section__heading">
            <div><RulerIcon :size="22" /><h2 id="review-size-title">定制尺寸</h2></div>
            <RouterLink :to="editLink('#custom-size')"><EditIcon :size="17" />修改</RouterLink>
          </div>
          <div class="review-dimensions">
            <div><span>{{ product.dimensionRanges.widthMm.label || '宽度' }}</span><strong>{{ centimeters(configuration.widthMm) }}</strong><small>cm</small></div>
            <div><span>{{ product.dimensionRanges.heightMm.label || '高度' }}</span><strong>{{ centimeters(configuration.heightMm) }}</strong><small>cm</small></div>
            <div><span>{{ product.dimensionRanges.depthMm.label || '深度' }}</span><strong>{{ centimeters(configuration.depthMm) }}</strong><small>cm</small></div>
          </div>
          <figure v-if="product.dimensionReferenceImage" class="review-reference">
            <img :src="product.dimensionReferenceImage.url" :alt="product.dimensionReferenceImage.alt" />
            <figcaption>{{ product.dimensionReferenceImage.name }}</figcaption>
          </figure>
        </section>

        <section class="review-section" aria-labelledby="review-hardware-title">
          <div class="review-section__heading">
            <div><WrenchIcon :size="22" /><h2 id="review-hardware-title">五金方案</h2></div>
            <RouterLink :to="editLink('#hardware')"><EditIcon :size="17" />修改</RouterLink>
          </div>
          <div v-if="selectedHardware" class="review-hardware">
            <div>
              <strong>{{ selectedHardware.name }}</strong>
              <span>{{ selectedHardware.brand }} {{ selectedHardware.model }}</span>
              <p>{{ selectedHardware.description }}</p>
            </div>
            <dl v-if="selectedHardware.specifications.length">
              <div v-for="item in selectedHardware.specifications" :key="item.componentKey || item.label">
                <dt>{{ item.label }}</dt>
                <dd>{{ item.value }}<small v-if="item.note">{{ item.note }}</small></dd>
              </div>
            </dl>
          </div>
          <p v-else class="review-empty">当前商品不需要选择五金。</p>
        </section>

        <section class="review-section" aria-labelledby="review-contact-title">
          <div class="review-section__heading">
            <div><UserIcon :size="22" /><h2 id="review-contact-title">联系信息</h2></div>
          </div>
          <form class="review-contact" @change="saveContact" @input="saveContact">
            <label><span>联系人</span><input v-model="contact.contactName" required autocomplete="name" placeholder="请填写姓名" /></label>
            <label><span>微信号（选填）</span><input v-model="contact.wechatId" autocomplete="off" placeholder="方便发送报价和效果图" /></label>
            <label><span>补充说明（选填）</span><textarea v-model="contact.notes" rows="3" placeholder="可以说明装修时间、墙面情况或其他需求"></textarea></label>
          </form>
        </section>

        <div v-if="validation && !validation.valid" class="review-validation" role="alert">
          <WarningIcon :size="22" weight="fill" />
          <div><strong>方案需要修改</strong><ul><li v-for="item in validation.messages" :key="item.message">{{ item.message }}</li></ul></div>
        </div>
        <p v-if="submitError" class="form-error review-submit-error" role="alert">{{ submitError }}</p>

        <div v-if="!submittedNo" class="customization-submit-bar">
          <div><strong>已确认定制内容</strong><small>客服二次确认后生产</small></div>
          <button class="button button--primary button--large" type="button" :disabled="submitting || validating" @click="submitCustomization">
            {{ submitting ? '正在提交' : '提交定制方案' }}
          </button>
        </div>

        <section v-else class="customization-success" role="status">
          <CheckCircleIcon :size="50" weight="fill" />
          <h2>定制方案已提交</h2>
          <p>方案编号：<strong>{{ submittedNo }}</strong></p>
          <span>客服会与您二次确认选色、尺寸和五金，确认完整后才会进入生产流程。</span>
          <RouterLink class="button button--primary" to="/orders">查看进度</RouterLink>
        </section>
      </div>
    </div>
  </section>
</template>
